import { createContext, useCallback, useContext, useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import type { ClinicModuleStateDto, ModuleName, PreviewModule } from "@dentacore/shared-types";

import { ApiError } from "../api/client";
import { readClinicModules, requestClinicModule } from "../api/clinic-modules";
import { readPreviews } from "../api/tasks";
import { MODULES_CHANGED } from "./module-events";

/** How soon a navigation may read the modules again — a switch is rare; a click is not. */
const NAVIGATION_REREAD_MS = 10_000;

interface Modules {
  /** Null until read, and when the read failed. */
  states: ClinicModuleStateDto[] | null;
  /**
   * **True only once the clinic is known to have it** — a read-only module counts
   * as had, because its screens still open (STORY-188c). While the read is in
   * flight, or if it failed, this is false, so a locked add-on's piece inside
   * another screen never shows before the answer arrives (QF-D9; it used to
   * fail open). Use `locked` to decide whether to draw something *as* locked.
   */
  has: (module: ModuleName) => boolean;
  /**
   * **Known not to be had** — what the sidebar's "Add-on" tag and the locked
   * page ask, so nothing is drawn locked while the answer is still coming.
   */
  locked: (module: ModuleName) => boolean;
  /** Switched off but its data kept, so it can be read and not changed (MOD-D18). */
  readOnly: (module: ModuleName) => boolean;
  /** On, so it can be changed. `has` is also true for a read-only module, whose screens still open. */
  writable: (module: ModuleName) => boolean;
  /** An admin has already asked for it. */
  requested: (module: ModuleName) => boolean;
  /** Ask for a module. Sends nothing; an operator sees the note. */
  request: (module: ModuleName) => Promise<void>;
  /**
   * A preview module switched on for this clinic (TK-D28). False while unknown:
   * a preview is hidden until it is known to be there, never shown and taken
   * away (TK-D36).
   */
  hasPreview: (module: PreviewModule) => boolean;
}

const ModulesContext = createContext<Modules>({
  states: null,
  has: () => false,
  locked: () => false,
  readOnly: () => false,
  writable: () => true,
  requested: () => false,
  request: () => Promise.resolve(),
  hasPreview: () => false,
});

/**
 * The add-on modules this clinic has, shared by the sidebar, the locked
 * screens and the Settings pane (STORY-188b, MOD-D11).
 *
 * **A teaser, not the check.** Every route behind a module refuses on its own
 * (STORY-188a); this only decides whether to draw an item as locked and what a
 * locked screen says. A failed read therefore shows nothing locked rather than
 * an error — the sidebar is ambient chrome.
 */
export function ModulesProvider({ children }: { children: React.ReactNode }): React.JSX.Element {
  const [states, setStates] = useState<ClinicModuleStateDto[] | null>(null);
  const [previews, setPreviews] = useState<PreviewModule[]>([]);
  const lastRead = useRef(0);

  /**
   * **Read again whenever the answer may have changed** (QA-13): an operator
   * switching a module used to leave an open tab drawing the old sidebar —
   * a locked Accounting the clinic now had, an Inventory it no longer had —
   * until somebody reloaded. So it is read on mount, when the tab comes back,
   * when any request is refused for a module, and on navigation at most every
   * ten seconds. A failed re-read keeps what was known rather than unlocking all.
   */
  const reload = useCallback((signal?: AbortSignal) => {
    lastRead.current = Date.now();
    readPreviews(signal)
      .then(setPreviews)
      .catch(() => {
        // Unknown stays hidden; the next read tries again.
      });
    readClinicModules(signal)
      .then(setStates)
      .catch(() => {
        // The first read failing leaves nothing locked (the teaser stays out of
        // the way); a later one failing keeps the states it already had.
      });
  }, []);

  useEffect(() => {
    const controller = new AbortController();
    reload(controller.signal);
    const onVisible = (): void => {
      if (document.visibilityState === "visible") reload();
    };
    const onChanged = (): void => reload();
    // A screen that keeps asking for a refused route must not make this read in
    // a loop: one re-read per two seconds is plenty to notice a switch.
    const onRefused = (): void => {
      if (Date.now() - lastRead.current > 2_000) reload();
    };
    document.addEventListener("visibilitychange", onVisible);
    window.addEventListener("focus", onChanged);
    window.addEventListener(MODULES_CHANGED, onRefused);
    return () => {
      controller.abort();
      document.removeEventListener("visibilitychange", onVisible);
      window.removeEventListener("focus", onChanged);
      window.removeEventListener(MODULES_CHANGED, onRefused);
    };
  }, [reload]);

  const { pathname } = useLocation();
  useEffect(() => {
    if (Date.now() - lastRead.current > NAVIGATION_REREAD_MS) reload();
  }, [pathname, reload]);

  const has = useCallback(
    (module: ModuleName) => {
      const state = states?.find((s) => s.module === module);
      return state === undefined ? false : state.enabled || state.readOnly;
    },
    [states],
  );
  const locked = useCallback(
    (module: ModuleName) => states !== null && !has(module),
    [states, has],
  );
  const readOnly = useCallback(
    (module: ModuleName) => states?.find((s) => s.module === module)?.readOnly ?? false,
    [states],
  );
  const writable = useCallback(
    (module: ModuleName) => states?.find((s) => s.module === module)?.enabled ?? false,
    [states],
  );
  const requested = useCallback(
    (module: ModuleName) => states?.find((s) => s.module === module)?.requested ?? false,
    [states],
  );
  const request = useCallback(
    async (module: ModuleName) => {
      try {
        setStates(await requestClinicModule(module));
      } catch (cause) {
        // Asked for one the clinic already has: switched on since this tab read
        // the list. Read it again, so the screen opens instead of saying both.
        if (cause instanceof ApiError && cause.status === 409) {
          reload();
          return;
        }
        throw cause;
      }
    },
    [reload],
  );

  const hasPreview = useCallback((module: PreviewModule) => previews.includes(module), [previews]);

  return (
    <ModulesContext.Provider
      value={{ states, has, locked, readOnly, writable, requested, request, hasPreview }}
    >
      {children}
    </ModulesContext.Provider>
  );
}

export function useModules(): Modules {
  return useContext(ModulesContext);
}
